import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const MOCK_POINTS = {
	total: 2990,
	pending: 310,
	validated: 2680,
	denied: 200,
	tier: 'Gold',
	rank: 3,
	totalUsers: 247,
	nextRank: { position: 2, pointsToReach: 220 },
	nextTier: { name: 'Platinum', pointsToReach: 510, progressPct: 85 },
	organization: { parent: 'Région Sud-Est', own: 'Équipe Lyon' },
	season: { id: 't2-2025', name: 'T2 2025', daysRemaining: 47 },
};

const MOCK_HISTORY = [
	{
		id: 'h1',
		date: '2026-05-12T09:14:00Z',
		type: 'NEGOTIATION',
		promoName: 'Promo BOGO Rayon Frais',
		location: 'Carrefour Lyon',
		pointsClaimed: 200,
		pointsValidated: null,
		status: 'PENDING',
		comment: 'Bonne mise en avant en TG, facing propre, ILV conforme.',
	},
	{
		id: 'h2',
		date: '2026-05-11T00:00:00Z',
		type: 'NEGOTIATION',
		promoName: 'Promo 3+1 Snacking',
		location: 'Opération été',
		pointsClaimed: 180,
		pointsValidated: 160,
		status: 'VALIDATED',
		validator: 'L. Petit',
	},
	{
		id: 'h3',
		date: '2026-05-10T00:00:00Z',
		type: 'BONUS',
		promoName: 'Meilleure progression semaine',
		location: 'Bonus système',
		pointsClaimed: 100,
		pointsValidated: 100,
		status: 'AUTO',
	},
	{
		id: 'h4',
		date: '2026-05-08T00:00:00Z',
		type: 'NEGOTIATION',
		promoName: 'Accord tête de gondole',
		location: 'Intermarché',
		validator: 'M. Dupont',
		pointsClaimed: 200,
		pointsValidated: 0,
		status: 'DENIED',
		deniedReason: 'Pas de preuve de mise en place fournie. Merci de photographier le linéaire la prochaine fois.',
	},
];

const pointsCache = new CacheManager('VueCache', 'cache', {
	maxAge: 5 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Points',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.points.`,
});

async function fetchOrCache(key, endpoint, fallback) {
	const memCached = pointsCache.getFromMemory(key);
	if (memCached) return memCached;

	const cached = await pointsCache.get(key);
	if (cached) {
		pointsCache.setInMemory(key, cached);
		return cached;
	}

	if (!iframeDataBridge.isOnline()) {
		log('info', 'Points', `📴 Offline: returning fallback for ${key}`);
		return fallback;
	}

	try {
		const response = null; //await authenticatedFetch(getApiUrl(endpoint), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await pointsCache.put(key, data);
		pointsCache.setInMemory(key, data);
		log('success', 'Points', `✅ Fetched ${key}`);
		return data;
	} catch (error) {
		log('warn', 'Points', `⚠️ API not available for ${key} - returning mock: ${error.message}`);
		return fallback;
	}
}

export async function getUserPoints(forceRefresh = false) {
	if (forceRefresh) {
		pointsCache.clearMemory();
		await pointsCache.clear();
	}
	return fetchOrCache('userPoints', 'Sales/UserPoints', MOCK_POINTS);
}

export async function getPointsHistory(forceRefresh = false) {
	if (forceRefresh) {
		pointsCache.clearMemory();
		await pointsCache.clear();
	}
	return fetchOrCache('history', 'Sales/PointsHistory', MOCK_HISTORY);
}

export async function claimPoints({ promoId, deliveryLocationId, pointsClaimed, comment }) {
	try {
		if (!iframeDataBridge.isOnline()) throw new Error('offline');

		const response = await authenticatedFetch(
			getApiUrl('Sales/ClaimPoints'),
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ promoId, deliveryLocationId, pointsClaimed, comment }),
			},
			'tokenSales'
		);
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const data = await response.json();
		await refreshPoints();
		log('success', 'Points', `✅ Claimed ${pointsClaimed} points for promo ${promoId}`);
		return data;
	} catch (error) {
		log('warn', 'Points', `⚠️ API not available - mock claim success: ${error.message}`);
		return { success: true, pointsClaimed };
	}
}

export async function updatePointsClaim({ claimId, pointsClaimed, comment }) {
	try {
		if (!iframeDataBridge.isOnline()) throw new Error('offline');

		const response = await authenticatedFetch(
			getApiUrl(`Sales/PointsClaim/${claimId}`),
			{
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ pointsClaimed, comment }),
			},
			'tokenSales'
		);
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

		const data = await response.json();
		await refreshPoints();
		log('success', 'Points', `✅ Updated claim ${claimId} to ${pointsClaimed} points`);
		return data;
	} catch (error) {
		log('warn', 'Points', `⚠️ API not available - mock update success: ${error.message}`);
		return { success: true, pointsClaimed };
	}
}

export async function refreshPoints() {
	pointsCache.clearMemory();
	await pointsCache.clear();
}
